import { zodResolver } from '@hookform/resolvers/zod';
import { FormControl, Button } from '@mui/material';
import { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { AgentDetailsUser, StyleObj } from '../../@types';
import useLocalization from '../../hooks/useLocalization';
import { useUpdateAgentData } from '../../queries';
import { FormTextField } from './form-elements';
import PasswordField from '../atoms/PasswordField';

const getProfileSchema = (t: TFunction) => {
  return z.object({
    firstName: z.string().trim().min(1, t('register.validation.firstNameRequired')).optional(),
    lastName: z.string().trim().min(1, t('register.validation.lastNameRequired')).optional(),
  });
};

const styles: StyleObj = {
  formControl: {
    display: 'flex',
    gap: 2,
    width: '100%',
    padding: 2,
    margin: 'auto',
  },
  saveButton: {
    borderRadius: 1,
    mt: 2,
  },
};

const UserDetailsForm = ({ userData }: { userData: AgentDetailsUser }) => {
  const { t } = useLocalization();
  const profileSchema = getProfileSchema(t);
  const { mutate: updateAgentData } = useUpdateAgentData(userData?.id || '');

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<AgentDetailsUser>({
    resolver: zodResolver(profileSchema),
    values: {
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
    },
  });

  const onSubmit = (data: AgentDetailsUser) => {
    updateAgentData(data);
  };

  return (
    <FormControl component='form' onSubmit={handleSubmit(onSubmit)} sx={styles.formControl}>
      <FormTextField
        name='firstName'
        control={control}
        label={t('firstName')}
        error={!!errors.firstName}
        helperText={errors.firstName?.message}
        fullWidth
      />
      <FormTextField
        name='lastName'
        control={control}
        label={t('lastName')}
        error={!!errors.lastName}
        helperText={errors.lastName?.message}
        fullWidth
      />
      <PasswordField sx={styles.textField} />
      {isDirty && (
        <Button type='submit' variant='contained' disabled={isSubmitting} sx={styles.saveButton}>
          {t('save')}
        </Button>
      )}
    </FormControl>
  );
};

export default UserDetailsForm;
